import type { MelodyChordLane, MelodyMeter } from '@tunasong/schemas'
import { Button, TextField } from '@tunasong/ui-lib'
import type { FC, KeyboardEvent } from 'react'
import { useId, useState } from 'react'
import { laneOfChords, parseChordNames, unrecognisedChordsLine } from './add-chords.js'
import styles from './chord-lane.module.css'

export interface AddChordsFieldProps {
  meter: MelodyMeter
  onAdd: (lane: MelodyChordLane) => void
}

/**
 * `Add chords` in a sketch's empty lane (melody toolkit M9.2; spec §7): the button opens a field for chord names, one
 * per bar; `Add chords` again, or Enter, adds them. All or nothing (Brian, 2026-11-02): a name it doesn't recognise
 * adds none, keeps the text, and says which, politely, under the field. The line describes the field, which is marked
 * invalid until the text changes (Joni, #3083). It stays in the accessibility tree while empty, so it is read when it
 * fills.
 */
export const AddChordsField: FC<AddChordsFieldProps> = props => {
  const { meter, onAdd } = props
  const [isOpen, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [problem, setProblem] = useState('')
  const problemId = useId()

  const add = () => {
    const parsed = parseChordNames(text)
    if (!parsed.ok) {
      setProblem(unrecognisedChordsLine(parsed.unrecognised))
      return
    }
    if (parsed.symbols.length === 0) {
      return
    }
    setProblem('')
    onAdd(laneOfChords(parsed.symbols, meter))
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      add()
    }
  }

  if (!isOpen) {
    return (
      <Button size="small" onClick={() => setOpen(true)}>
        Add chords
      </Button>
    )
  }

  return (
    <div className={styles.addChords}>
      <TextField
        size="small"
        label="Chords, one per bar"
        placeholder="C Am F G"
        value={text}
        autoFocus={true}
        aria-describedby={problemId}
        aria-invalid={problem !== ''}
        onChange={event => {
          setText(event.target.value)
          setProblem('')
        }}
        onKeyDown={handleKeyDown}
      />
      <Button size="small" onClick={add}>
        Add chords
      </Button>
      <p id={problemId} className={styles.problem} role="status" aria-live="polite">
        {problem}
      </p>
    </div>
  )
}
